import {
  ADJUSTMENTS_UNSUPPORTED_MESSAGE,
  NodeKind,
  TransformKind,
  adjustmentsToCanvasFilter,
  type CompositionNode,
  type Document,
  type OrientationParams,
  type SourceId,
} from '@/core/document'
import {
  IDENTITY_ORIENTATION,
  assertPositiveSize,
  orientationMatrix,
  orientedSize,
  type Size,
} from '@/core/geometry'
import { nativeSize, nodeRenderRect } from '@/core/document/tree'
import { renderPolaroid } from '@/tools/frame/renderPolaroid'
import { FrameStyleId } from '@/tools/frame/params'
import { renderPlainFrame } from '@/tools/frame/renderPlainFrame'
import { renderCollage } from '@/tools/collage/renderCollage'
import { RenderUnsupportedError } from '@/core/render/errors'

export type RenderContext = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D

export interface NodeRenderSource extends Size {
  bitmap: ImageBitmap
}

export function drawOrientedImage(
  source: NodeRenderSource,
  orientation: OrientationParams,
  context: RenderContext,
  bitmap: CanvasImageSource = source.bitmap,
): void {
  const matrix = orientationMatrix(source, orientation)
  context.transform(matrix.a, matrix.b, matrix.c, matrix.d, matrix.e, matrix.f)
  context.drawImage(bitmap, 0, 0, source.width, source.height)
}

export function renderNode(
  node: CompositionNode,
  _document: Document,
  sources: ReadonlyMap<SourceId, NodeRenderSource>,
  targetSize: Size,
  context: RenderContext,
): void {
  assertPositiveSize(targetSize, 'Node render target')
  const renderChildInContainer = (
    child: CompositionNode,
    containerSize: Size,
    childContext: RenderContext,
  ): void => {
    const rect = nodeRenderRect(_document, sources, child, containerSize)
    childContext.save()
    try {
      childContext.translate(rect.x, rect.y)
      renderNode(child, _document, sources, { width: rect.width, height: rect.height }, childContext)
    } finally {
      childContext.restore()
    }
  }
  switch (node.kind) {
    case NodeKind.Image: {
      const source = sources.get(node.sourceId)
      if (!source) throw new Error(`Cannot render image node "${node.id}": source "${node.sourceId}" is missing`)
      const orientation = node.transforms[TransformKind.Orientation] ?? IDENTITY_ORIENTATION
      const crop = node.transforms[TransformKind.Crop]
      const sourceSize = orientedSize(source, orientation)
      const sourceRect = crop?.rect ?? { x: 0, y: 0, ...sourceSize }
      const adjustments = node.transforms[TransformKind.Adjustments]
      const filter = adjustments ? adjustmentsToCanvasFilter(adjustments) : ''
      const scale = Math.max(targetSize.width / sourceRect.width, targetSize.height / sourceRect.height)
      const width = sourceRect.width * scale
      const height = sourceRect.height * scale
      context.save()
      try {
        if (filter) {
          if (!('filter' in context)) {
            throw new RenderUnsupportedError('Canvas 2D filters', ADJUSTMENTS_UNSUPPORTED_MESSAGE)
          }
          context.filter = filter
          if (context.filter !== filter) {
            throw new RenderUnsupportedError('Canvas 2D filters', ADJUSTMENTS_UNSUPPORTED_MESSAGE)
          }
        }
        context.translate((targetSize.width - width) / 2, (targetSize.height - height) / 2)
        context.scale(scale, scale)
        context.translate(-sourceRect.x, -sourceRect.y)
        drawOrientedImage(source, orientation, context)
      } finally {
        context.restore()
      }
      return
    }
    case NodeKind.Frame: {
      if (node.params.styleId === FrameStyleId.Plain) {
        renderPlainFrame({ ...node, params: node.params }, targetSize, context, (child, size, childContext) => {
          renderChildInContainer(child, size, childContext)
        })
        return
      }
      const childSize = nativeSize(_document, sources, node.child.id)
      renderPolaroid({ ...node, params: node.params }, childSize, targetSize, context, (child, size, childContext) => {
        renderChildInContainer(child, size, childContext)
      })
      return
    }
    case NodeKind.Collage:
      renderCollage(node, targetSize, sources, context, (child, size, childContext) => {
        renderChildInContainer(child, size, childContext)
      })
      return
    default:
      throw new Error(`Cannot render unknown node kind "${String((node as { kind: unknown }).kind)}"`)
  }
}
